import React from 'react';
import { combineClasses } from '../../utils/formatters';
import { LAYOUT_CONFIG } from '../../config/layoutConfig';
import { LayoutPropTypes, LayoutDefaultProps } from './propTypes';
import { Card, Alert } from '../ui';
import Footer from './Footer';

// Base Layout Component - Optimized and configurable
const Layout = React.memo(({ 
  children, 
  title, 
  subtitle, 
  className = '',
  maxWidth = LAYOUT_CONFIG.RESPONSIVE.maxWidth.sm,
  showFooter = true 
}) => {
  return (
    <div className={LAYOUT_CONFIG.MAIN.container}>
      <div className={LAYOUT_CONFIG.MAIN.content}>
        <div className={combineClasses(
          LAYOUT_CONFIG.RESPONSIVE.margin.auto,
          "w-full",
          maxWidth
        )}>
          {title && (
            <div className={LAYOUT_CONFIG.TYPOGRAPHY.textCenter}>
              <h2 className={LAYOUT_CONFIG.TYPOGRAPHY.title}>
                {title}
              </h2>
              {subtitle && (
                <p className={LAYOUT_CONFIG.TYPOGRAPHY.subtitle}>
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        <div className={combineClasses(
          LAYOUT_CONFIG.RESPONSIVE.margin.top.lg,
          LAYOUT_CONFIG.RESPONSIVE.margin.auto,
          "w-full",
          maxWidth
        )}>
          <Card className={className}>
            <Card.Content className={LAYOUT_CONFIG.CARD.content}>
              {children}
            </Card.Content>
          </Card>
        </div>
      </div>
      
      {/* Footer */}
      {showFooter && <Footer />}
    </div>
  );
});

Layout.displayName = 'Layout';
Layout.propTypes = LayoutPropTypes.layout;
Layout.defaultProps = LayoutDefaultProps.layout;

// Auth Layout Component - Enhanced with better error handling
const AuthLayout = React.memo(({ 
  children, 
  title, 
  subtitle, 
  alert, 
  onAlertClose,
  maxWidth = LAYOUT_CONFIG.RESPONSIVE.maxWidth.sm,
  showFooter = true
}) => {
  return (
    <Layout 
      title={title} 
      subtitle={subtitle} 
      maxWidth={maxWidth}
      showFooter={showFooter}
    >
      {alert && (
        <div className={LAYOUT_CONFIG.ALERT.container}>
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={onAlertClose}
            aria-live="polite"
            role="alert"
          />
        </div>
      )}
      {children}
    </Layout>
  );
});

AuthLayout.displayName = 'AuthLayout';
AuthLayout.propTypes = LayoutPropTypes.authLayout;
AuthLayout.defaultProps = LayoutDefaultProps.authLayout;

// Page Layout Component - Enhanced and configurable
const PageLayout = React.memo(({ 
  children, 
  header, 
  className = '',
  containerMaxWidth = LAYOUT_CONFIG.PAGE.headerContainer,
  mainMaxWidth = LAYOUT_CONFIG.PAGE.mainContainer,
  showHeader = Boolean(header)
}) => {
  return (
    <div className={combineClasses(LAYOUT_CONFIG.PAGE.container, className)}>
      {showHeader && header && (
        <header className={LAYOUT_CONFIG.PAGE.headerBg}>
          <div className={containerMaxWidth}>
            {header}
          </div>
        </header>
      )}
      <main className={mainMaxWidth}>
        {children}
      </main>
    </div>
  );
});

PageLayout.displayName = 'PageLayout';
PageLayout.propTypes = LayoutPropTypes.pageLayout;
PageLayout.defaultProps = LayoutDefaultProps.pageLayout;

// Layout with TopBar - New enhanced layout with integrated TopBar
const MainLayout = React.memo(({
  children,
  topBarProps = {},
  showTopBar = true,
  className = '',
  containerMaxWidth = LAYOUT_CONFIG.PAGE.headerContainer,
  mainMaxWidth = LAYOUT_CONFIG.PAGE.mainContainer
}) => {
  // Dynamic import for TopBar to enable code splitting
  const [TopBar, setTopBar] = React.useState(null);
  
  React.useEffect(() => {
    if (showTopBar) {
      import('./TopBar').then(module => {
        setTopBar(() => module.default);
      });
    }
  }, [showTopBar]);

  return (
    <div className={combineClasses(LAYOUT_CONFIG.PAGE.container, className)}>
      {showTopBar && TopBar && (
        <TopBar 
          containerMaxWidth={containerMaxWidth}
          {...topBarProps} 
        />
      )}
      <main className={mainMaxWidth}>
        {children}
      </main>
    </div>
  );
});

MainLayout.displayName = 'MainLayout';
MainLayout.propTypes = LayoutPropTypes.mainLayout;
MainLayout.defaultProps = LayoutDefaultProps.mainLayout;

// Exports - Enhanced with new MainLayout
export { Layout, AuthLayout, PageLayout, MainLayout };
export { default as TopBar } from './TopBar';
export { LAYOUT_CONFIG } from '../../config/layoutConfig';
